import { ReactNode, Ref } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './CollapsibleSection.scss';

export interface ICollapsibleSectionProps {
    /** Stable id; ties the toggle to the body it opens. */
    id: string;
    title: string;
    /** The sentence behind the 'i' next to the title. */
    hint?: string;
    /**
     * What stays readable while the section is folded: a state pill, a count, a one-line
     * verdict. Whatever a reader needs in order to decide whether to open it at all.
     */
    status?: ReactNode;
    /** Controls belonging to this section, drawn only while it is open. */
    tools?: ReactNode;
    open: boolean;
    onToggle: () => void;
    /** Extra class on the section, so a panel keeps its own body styles. */
    className?: string;
    /** Handed through so the page can scroll to this section. */
    innerRef?: Ref<HTMLElement>;
    /** Whether something is on its way inside this section. */
    busy?: boolean;
    children: ReactNode;
}

/**
 * One section of the detail page: a header that is always readable and a body that folds
 * away.
 *
 * The page grew to six panels, each with its own header built its own way, and a screen you
 * have to scroll past four of them to reach the fifth. Folding is what fixes that, and it
 * only works when every section folds the same way - which is why the chrome lives here
 * rather than in each panel.
 *
 * The body stays mounted while it is folded. It holds typed-in ids, a search term and an
 * opened queue card, and unmounting would throw those away every time somebody folds a
 * section up to get it out of the way. `hidden` is enough: it takes the content out of the
 * layout and out of the accessibility tree, without taking it out of React.
 */
export const CollapsibleSection = ({
    id,
    title,
    hint,
    status,
    tools,
    open,
    onToggle,
    className,
    innerRef,
    busy,
    children,
}: ICollapsibleSectionProps) => (
    <section
        className={`collapsible${open ? '' : ' collapsible--closed'}${
            className ? ` ${className}` : ''
        }`}
        ref={innerRef}
        aria-busy={busy}
    >
        <div className="collapsible__head">
            {/*
                The heading carries the button rather than the other way round: a heading
                inside a button is not phrasing content, and screen readers lose the outline
                of the page with it.
            */}
            <h3 className="collapsible__title">
                <button
                    type="button"
                    className="collapsible__toggle"
                    aria-expanded={open}
                    aria-controls={`${id}-body`}
                    onClick={onToggle}
                >
                    <FontAwesomeIcon
                        icon={open ? 'angle-down' : 'angle-right'}
                        className="collapsible__chevron"
                    />
                    {title}
                </button>
            </h3>

            {hint && (
                <span
                    className="collapsible__hint"
                    tabIndex={0}
                    role="note"
                    title={hint}
                    aria-label={hint}
                >
                    <FontAwesomeIcon icon="circle-info" />
                </span>
            )}

            {status && <span className="collapsible__status">{status}</span>}

            {open && tools && <div className="collapsible__tools">{tools}</div>}
        </div>

        <div className="collapsible__body" id={`${id}-body`} hidden={!open}>
            {children}
        </div>
    </section>
);
